import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertTriangle, RefreshCw, Database, Loader2, 
  Plus, Mail, FileText, Users, 
  Building2, User, Shield,
  CreditCard, Check, Lock, X, Receipt
} from 'lucide-react';
import { allowedEmailsAPI, adminAPI, accountsAPI, taxConfigAPI, organizationAPI, creditsAPI, checkoutAPI, couponAPI, billingAPI, invoicesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import ProfileTab from '../components/settings/ProfileTab';
import BillingConfigTab from '../components/settings/BillingConfigTab';
import SubscriptionTab from '../components/settings/SubscriptionTab';
import OrgTab from '../components/settings/OrgTab';
import TaxTab from '../components/settings/TaxTab';
import DataTab from '../components/settings/DataTab';
import TeamManagement from '../components/TeamManagement';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

function AutoApprovalToggle({ org, setOrg, refreshOrg }) {
  const [eligibility, setEligibility] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [showWarning, setShowWarning] = React.useState(false);

  React.useEffect(() => {
    organizationAPI.getAutoApprovalEligibility().then(res => {
      setEligibility(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />;

  const isEnabled = org.auto_approve_enabled || false;
  const isEligible = eligibility?.eligible || false;

  return (
    <div className="flex flex-col items-end gap-2 ml-4 flex-shrink-0">
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={isEnabled}
          disabled={!isEligible && !isEnabled}
          onChange={async (e) => {
            if (e.target.checked && !isEnabled) {
              setShowWarning(true);
              return;
            }
            try {
              const res = await organizationAPI.update({ auto_approve_enabled: false });
              setOrg(res.data); refreshOrg();
              toast.success('Auto-approve disabled');
            } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
          }}
          className="sr-only peer"
          data-testid="auto-approve-toggle"
        />
        <div className={`w-11 h-6 rounded-full peer after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all ${isEnabled ? 'bg-[#D94E18] peer-checked:after:translate-x-full' : isEligible ? 'bg-zinc-200' : 'bg-zinc-100 cursor-not-allowed'}`}></div>
      </label>

      {!isEligible && !isEnabled && eligibility && (
        <div className="text-right max-w-[200px]">
          <p className="text-[10px] text-zinc-400 leading-snug">{eligibility.transactions_reviewed}/{eligibility.transactions_required} transactions</p>
          <div className="w-full bg-zinc-100 rounded-full h-1.5 mt-1">
            <div className="bg-[#D94E18] h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (eligibility.transactions_reviewed / eligibility.transactions_required) * 100)}%` }} />
          </div>
          {eligibility.transactions_reviewed >= eligibility.transactions_required && (
            <p className="text-[10px] text-amber-600 mt-0.5">Accuracy: {eligibility.accuracy}% (need {eligibility.accuracy_required}%)</p>
          )}
        </div>
      )}

      {showWarning && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowWarning(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md shadow-xl" onClick={e => e.stopPropagation()} data-testid="auto-approve-warning">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h3 className="text-base font-semibold text-zinc-900">Enable Auto-Approval?</h3>
            </div>
            <p className="text-sm text-zinc-600 leading-relaxed mb-4">
              We strongly recommend enabling this only after Kaya has learned your accounting patterns from your corrections over several months. Enabling too early may result in misclassified transactions and incorrect financial statements.
            </p>
            <p className="text-xs text-zinc-400 mb-4">
              Current AI accuracy: <span className="font-medium text-zinc-600">{eligibility?.accuracy}%</span> across {eligibility?.transactions_reviewed} reviewed transactions.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowWarning(false)}>Cancel</Button>
              <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="confirm-auto-approve-btn"
                onClick={async () => {
                  try {
                    const res = await organizationAPI.update({ auto_approve_enabled: true });
                    setOrg(res.data); refreshOrg(); setShowWarning(false);
                    toast.success('Auto-approve enabled — monitor your transactions closely.');
                  } catch (err) { toast.error(err.response?.data?.detail || 'Failed to enable'); }
                }}
              >I understand the risks — enable</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'subscription', label: 'Plan & Credits', icon: CreditCard },
  { id: 'org', label: 'Company', icon: Building2 },
  { id: 'billing', label: 'Invoice Settings', icon: Receipt },
  { id: 'tax', label: 'Tax / VAT', icon: FileText, businessOnly: true },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'data', label: 'Data', icon: Database },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const { isPersonal, refreshOrg, entities, switchEntity, canCreateEntity, maxEntities } = useOrg();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);

  // Shared state
  const [stats, setStats] = useState(null);
  const [taxConfig, setTaxConfig] = useState(null);
  const [taxPresets, setTaxPresets] = useState([]);
  const [org, setOrg] = useState(null);
  const [creditInfo, setCreditInfo] = useState(null);
  const [billingConfig, setBillingConfig] = useState(null);
  const [allAccounts, setAllAccounts] = useState([]);

  // Org tab state
  const [editingOrg, setEditingOrg] = useState(false);
  const [orgForm, setOrgForm] = useState({});
  const [processing, setProcessing] = useState(false);
  const [showNewEntityDialog, setShowNewEntityDialog] = useState(false);
  const [newEntityName, setNewEntityName] = useState('');
  const [creatingEntity, setCreatingEntity] = useState(false);
  const [fyCloseWizard, setFyCloseWizard] = useState({ open: false, year: null, step: 'preview', loading: false, preview: null, result: null });

  // Subscription state
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutResult, setCheckoutResult] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [redeemingCoupon, setRedeemingCoupon] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, taxRes, taxPresetsRes, orgRes, creditsRes, bcRes, acctRes] = await Promise.all([
        adminAPI.getStats().catch(() => ({ data: null })),
        taxConfigAPI.getActive().catch(() => ({ data: null })),
        taxConfigAPI.getPresets().catch(() => ({ data: [] })),
        organizationAPI.get().catch(() => ({ data: null })),
        creditsAPI.get().catch(() => ({ data: null })),
        invoicesAPI.getBillingConfig().catch(() => ({ data: null })),
        accountsAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setTaxConfig(taxRes.data);
      setTaxPresets(taxPresetsRes.data || []);
      setOrg(orgRes.data);
      setCreditInfo(creditsRes.data);
      if (bcRes.data) setBillingConfig(bcRes.data);
      setAllAccounts(acctRes.data?.items || acctRes.data || []);
    } catch (error) {
      console.error('Failed to load settings data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const tab = params.get('tab');
    if (tab === 'subscription') setActiveTab('subscription');
    if (!sessionId) return;

    let cancelled = false;
    const pollStatus = async () => {
      try {
        const res = await checkoutAPI.status(sessionId);
        const { payment_status, metadata } = res.data;
        if (payment_status === 'paid' && !cancelled) {
          const meta = metadata || {};
          if (meta.type === 'topup') {
            setCheckoutResult({ type: 'topup', status: 'paid', credits: meta.credits });
            toast.success(`${meta.credits} credits added to your account!`);
          } else if (meta.type === 'subscription') {
            setCheckoutResult({ type: 'subscription', status: 'paid', tier: meta.tier });
            toast.success(`Upgraded to ${meta.tier} plan!`);
          }
          window.history.replaceState({}, '', '/settings?tab=subscription');
          await loadData();
        } else if (payment_status === 'unpaid' || payment_status === 'open') {
          if (!cancelled) setTimeout(pollStatus, 2000);
          return;
        } else {
          toast.error('Payment was not completed. Please try again.');
          window.history.replaceState({}, '', '/settings?tab=subscription');
        }
      } catch {
        toast.error('Could not verify payment status.');
        window.history.replaceState({}, '', '/settings?tab=subscription');
      }
    };
    setActiveTab('subscription');
    pollStatus();
    return () => { cancelled = true; };
  }, [loadData]);

  // Subscription handlers
  const handleBuyCredits = async (credits) => {
    setCheckoutLoading(`topup-${credits}`);
    try {
      const res = await creditsAPI.topup(credits);
      if (res.data?.url) window.location.href = res.data.url;
      else toast.error('Failed to create checkout session');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to initiate payment'); }
    finally { setCheckoutLoading(null); }
  };

  const handleUpgrade = async (tier) => {
    setCheckoutLoading(`subscribe-${tier}`);
    try {
      // If user already has a Stripe subscription, modify it immediately (prorated)
      if (org?.stripe_subscription_id) {
        const res = await billingAPI.upgrade(tier);
        toast.success(res.data.message || `Upgraded to ${tier}`);
        await loadData();
      } else {
        // No existing subscription — create Stripe Checkout session
        const res = await billingAPI.subscribe(tier);
        if (res.data?.url) window.location.href = res.data.url;
        else toast.error('Failed to create checkout session');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to initiate upgrade'); }
    finally { setCheckoutLoading(null); }
  };

  const handleDowngrade = async (tier) => {
    setCheckoutLoading(`downgrade-${tier}`);
    try {
      const res = await billingAPI.downgrade(tier);
      toast.success(res.data.message);
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to downgrade'); }
    finally { setCheckoutLoading(null); }
  };

  const handleCancelSubscription = async () => {
    setCheckoutLoading('cancel');
    try {
      const res = await billingAPI.cancel();
      toast.success(res.data.message);
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel'); }
    finally { setCheckoutLoading(null); }
  };

  const handleReactivate = async () => {
    setCheckoutLoading('reactivate');
    try {
      const res = await billingAPI.reactivate();
      toast.success(res.data.message);
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reactivate'); }
    finally { setCheckoutLoading(null); }
  };

  const handleManageBilling = async () => {
    try {
      const res = await billingAPI.getPortalUrl();
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Billing portal not available'); }
  };

  const handleRedeemCoupon = async () => {
    setRedeemingCoupon(true);
    try {
      const res = await couponAPI.redeem(couponCode.trim());
      toast.success(res.data.message);
      setCouponCode('');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to redeem coupon'); }
    setRedeemingCoupon(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#D94E18] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const visibleTabs = TABS.filter(t => !t.businessOnly || !isPersonal);

  return (
    <div className="animate-fade-in" data-testid="settings-page">
      {/* Mobile tab row */}
      <div className="sm:hidden flex gap-1 overflow-x-auto pb-2 mb-4 -mx-1 px-1">
        {visibleTabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id ? 'bg-[#D94E18] text-white' : 'bg-zinc-100 text-zinc-600'
              }`}>
              <Icon className="w-3.5 h-3.5" />{tab.label}
            </button>
          );
        })}
      </div>

      <div className="flex gap-6">
        {/* Desktop nav */}
        <nav className="w-48 flex-shrink-0 hidden sm:block" data-testid="settings-nav">
          <div className="sticky top-4 space-y-1">
            {visibleTabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`settings-tab-${tab.id}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                    isActive ? 'bg-[#D94E18]/10 text-[#D94E18]' : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'
                  }`}>
                  <Icon className="w-4 h-4" />{tab.label}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-6">
          {activeTab === 'profile' && <ProfileTab user={user} org={org} stats={stats} />}

          {activeTab === 'subscription' && (
            <SubscriptionTab
              creditInfo={creditInfo} org={org}
              checkoutLoading={checkoutLoading} checkoutResult={checkoutResult} setCheckoutResult={setCheckoutResult}
              couponCode={couponCode} setCouponCode={setCouponCode} redeemingCoupon={redeemingCoupon}
              handleUpgrade={handleUpgrade} handleDowngrade={handleDowngrade} handleBuyCredits={handleBuyCredits} handleRedeemCoupon={handleRedeemCoupon}
              handleManageBilling={handleManageBilling} handleCancelSubscription={handleCancelSubscription} handleReactivate={handleReactivate}
            />
          )}

          {activeTab === 'org' && (
            <OrgTab
              org={org} setOrg={setOrg}
              editingOrg={editingOrg} setEditingOrg={setEditingOrg}
              orgForm={orgForm} setOrgForm={setOrgForm}
              processing={processing} setProcessing={setProcessing}
              loadData={loadData}
              entities={entities} switchEntity={switchEntity}
              canCreateEntity={canCreateEntity} maxEntities={maxEntities}
              setShowNewEntityDialog={setShowNewEntityDialog}
              fyCloseWizard={fyCloseWizard} setFyCloseWizard={setFyCloseWizard}
              AutoApprovalToggle={AutoApprovalToggle}
            />
          )}

          {activeTab === 'tax' && !isPersonal && (
            <TaxTab taxConfig={taxConfig} setTaxConfig={setTaxConfig} taxPresets={taxPresets} org={org} loadData={loadData} />
          )}

          {activeTab === 'billing' && (
            <BillingConfigTab billingConfig={billingConfig} setBillingConfig={setBillingConfig} allAccounts={allAccounts} />
          )}

          {activeTab === 'team' && <TeamManagement />}

          {activeTab === 'data' && <DataTab stats={stats} loadData={loadData} />}
        </div>
      </div>

      {/* FY Close Wizard Dialog */}
      <Dialog open={fyCloseWizard.open} onOpenChange={(open) => { if (!open) setFyCloseWizard(prev => ({ ...prev, open: false })); }}>
        <DialogContent className="max-w-lg" data-testid="fy-close-wizard">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#D94E18]" />
              {fyCloseWizard.result ? `FY ${fyCloseWizard.year} Closed` : `Close Fiscal Year ${fyCloseWizard.year}`}
            </DialogTitle>
          </DialogHeader>

          {fyCloseWizard.loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#D94E18]" />
              <span className="ml-2 text-sm text-zinc-500">Computing closing entries...</span>
            </div>
          )}

          {fyCloseWizard.result && (
            <div className="space-y-4 py-2" data-testid="fy-close-result">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  <p className="text-sm font-medium text-emerald-800">{fyCloseWizard.result.message}</p>
                </div>
                {fyCloseWizard.result.entry_number && (
                  <p className="text-xs text-emerald-600 mt-1 ml-7">Journal Entry #{fyCloseWizard.result.entry_number}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-zinc-50 rounded-lg text-center">
                  <p className="text-lg font-semibold text-zinc-900">{(fyCloseWizard.result.net_income || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                  <p className="text-xs text-zinc-500">{fyCloseWizard.result.net_income >= 0 ? 'Net Income' : 'Net Loss'} transferred</p>
                </div>
                <div className="p-3 bg-zinc-50 rounded-lg text-center">
                  <p className="text-lg font-semibold text-zinc-900">{fyCloseWizard.result.lines_count || 0}</p>
                  <p className="text-xs text-zinc-500">Closing lines</p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={async () => { setFyCloseWizard(prev => ({ ...prev, open: false })); const res = await organizationAPI.get(); setOrg(res.data); }} className="bg-[#D94E18] hover:bg-[#C2410C] text-white" data-testid="fy-close-done-btn">Done</Button>
              </DialogFooter>
            </div>
          )}

          {!fyCloseWizard.loading && fyCloseWizard.preview && !fyCloseWizard.result && (
            <div className="space-y-4 py-2" data-testid="fy-close-preview">
              <p className="text-sm text-zinc-600">Period: <span className="font-medium">{fyCloseWizard.preview.period}</span></p>

              {fyCloseWizard.preview.has_activity ? (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-emerald-50 rounded-lg text-center">
                      <p className="text-sm font-semibold text-emerald-700">{fyCloseWizard.preview.total_revenue?.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      <p className="text-xs text-emerald-600">Revenue</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg text-center">
                      <p className="text-sm font-semibold text-red-700">{fyCloseWizard.preview.total_expenses?.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      <p className="text-xs text-red-600">Expenses</p>
                    </div>
                    <div className="p-3 bg-zinc-50 rounded-lg text-center">
                      <p className={`text-sm font-semibold ${fyCloseWizard.preview.net_income >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {fyCloseWizard.preview.net_income?.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </p>
                      <p className="text-xs text-zinc-500">{fyCloseWizard.preview.net_income >= 0 ? 'Net Income' : 'Net Loss'}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">Closing entries to be generated</p>
                    <div className="max-h-48 overflow-y-auto border border-zinc-200 rounded-lg divide-y divide-zinc-100">
                      {fyCloseWizard.preview.closing_lines.map((line) => (
                        <div key={`${line.account_code}-${line.debit}-${line.credit}`} className="flex items-center justify-between px-3 py-2 text-xs">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${line.account_type === 'revenue' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            <span className="font-mono text-zinc-400">{line.account_code}</span>
                            <span className="text-zinc-700 truncate">{line.account_name}</span>
                          </div>
                          <div className="flex gap-4 flex-shrink-0 ml-2">
                            <span className="text-zinc-500 w-20 text-right">{line.debit > 0 ? line.debit.toLocaleString(undefined, {minimumFractionDigits: 2}) : ''}</span>
                            <span className="text-zinc-500 w-20 text-right">{line.credit > 0 ? line.credit.toLocaleString(undefined, {minimumFractionDigits: 2}) : ''}</span>
                          </div>
                        </div>
                      ))}
                      <div className="flex items-center justify-between px-3 py-2 text-xs bg-zinc-50 font-medium">
                        <div className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                          <span className="font-mono text-zinc-400">{fyCloseWizard.preview.retained_earnings_account?.code || '3900'}</span>
                          <span className="text-zinc-700">{fyCloseWizard.preview.retained_earnings_account?.name || 'Retained Earnings'}</span>
                        </div>
                        <div className="flex gap-4 flex-shrink-0 ml-2">
                          <span className="w-20 text-right">{fyCloseWizard.preview.net_income < 0 ? Math.abs(fyCloseWizard.preview.net_income).toLocaleString(undefined, {minimumFractionDigits: 2}) : ''}</span>
                          <span className="w-20 text-right">{fyCloseWizard.preview.net_income >= 0 ? fyCloseWizard.preview.net_income.toLocaleString(undefined, {minimumFractionDigits: 2}) : ''}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-8 px-3 py-1.5 text-xs font-medium text-zinc-400">
                      <span>Debit</span><span>Credit</span>
                    </div>
                  </div>

                  <p className="text-xs text-zinc-400">
                    This will zero out all revenue & expense accounts for FY {fyCloseWizard.year} and transfer the net {fyCloseWizard.preview.net_income >= 0 ? 'income' : 'loss'} to Retained Earnings.
                  </p>
                </>
              ) : (
                <div className="p-4 bg-zinc-50 rounded-lg text-center">
                  <p className="text-sm text-zinc-500">No revenue or expense activity found for FY {fyCloseWizard.year}.</p>
                  <p className="text-xs text-zinc-400 mt-1">The year will be locked without generating closing entries.</p>
                </div>
              )}

              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="outline" size="sm" onClick={() => setFyCloseWizard(prev => ({ ...prev, open: false }))} data-testid="fy-close-cancel-btn">Cancel</Button>
                {fyCloseWizard.preview.has_activity && (
                  <Button variant="outline" size="sm" disabled={fyCloseWizard.loading}
                    onClick={async () => {
                      setFyCloseWizard(prev => ({ ...prev, loading: true }));
                      try {
                        await organizationAPI.lockFiscalYear(fyCloseWizard.year);
                        const res = await organizationAPI.get();
                        setOrg(res.data);
                        setFyCloseWizard(prev => ({ ...prev, open: false }));
                        toast.success(`FY ${fyCloseWizard.year} locked (no closing entries generated).`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to lock');
                        setFyCloseWizard(prev => ({ ...prev, loading: false }));
                      }
                    }}
                    data-testid="fy-lock-only-btn">Lock Only</Button>
                )}
                <Button size="sm" className="bg-[#D94E18] hover:bg-[#C2410C] text-white" disabled={fyCloseWizard.loading}
                  onClick={async () => {
                    setFyCloseWizard(prev => ({ ...prev, loading: true }));
                    try {
                      const res = await organizationAPI.closeFiscalYear(fyCloseWizard.year);
                      setFyCloseWizard(prev => ({ ...prev, loading: false, step: 'result', result: res.data }));
                      toast.success(res.data.message);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to close fiscal year');
                      setFyCloseWizard(prev => ({ ...prev, loading: false }));
                    }
                  }}
                  data-testid="fy-close-confirm-btn">
                  {fyCloseWizard.loading && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                  {fyCloseWizard.preview?.has_activity ? 'Close & Lock' : 'Lock Year'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Entity Dialog */}
      <Dialog open={showNewEntityDialog} onOpenChange={setShowNewEntityDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>New Legal Entity</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs font-semibold text-zinc-600">Company Name</Label>
            <Input value={newEntityName} onChange={e => setNewEntityName(e.target.value)} placeholder="e.g. Acme Holdings GmbH" className="mt-1" data-testid="settings-new-entity-name" />
            <p className="text-xs text-zinc-400 mt-2">The new entity will inherit your current country and pricing tier.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewEntityDialog(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!newEntityName.trim()) return toast.error('Entity name required');
                setCreatingEntity(true);
                try {
                  await organizationAPI.createEntity({ company_name: newEntityName.trim() });
                  toast.success('Entity created');
                  setShowNewEntityDialog(false);
                  setNewEntityName('');
                  refreshOrg();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create entity'); }
                setCreatingEntity(false);
              }}
              disabled={creatingEntity}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="settings-submit-entity">
              {creatingEntity ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
